import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import { keywordGroupsTransforms } from "app/services/keyword-groups/utils/keywordGroupsTransforms";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEVICES_TYPES_FOR_API, EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import {
    DEFAULT_SORT,
    STATS_COMPOSITION_ENDPOINT,
} from "app/common/keyword-research/keywords-overview/serpCompositionConsts";
import type { IStatsResponseData } from "app/common/keyword-research/keywords-overview/serpCompositionTypes";

// Base hook that fetches raw API data - reusable for both SERP and Intent
export const useOverviewStatsData = () => {
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country, webSource } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const apiParams = {
        to,
        from,
        isWindow,
        country,
        webSource,
        key: isKeywordGroup ? keyword.substring(1) : keyword,
        sort: DEFAULT_SORT,
        asc: false,
        includeSubDomains: true,
        Device: webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : webSource,
    };

    return useQuery<IStatsResponseData>({
        queryKey: [STATS_COMPOSITION_ENDPOINT, apiParams],
        queryFn: async () => {
            try {
                const serpData = await DefaultFetchService.getInstance().get<IStatsResponseData>(
                    STATS_COMPOSITION_ENDPOINT,
                    apiParams,
                );
                return serpData;
            } catch (error: unknown) {
                Sentry.errorTracker({
                    message: "Error fetching overview stats data",
                    e: error,
                    level: "error",
                    params: {
                        extra: {
                            apiParams,
                        },
                    },
                });
                throw error;
            }
        },
    });
};
